import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';

import { UserEvent as PROTOBUF_UserEvent } from '../__generated__/protobuf/journey/data/user_event';
import { UserEvent } from '../declarations';
import { ContextualData } from '../contextualData';
import { getDeployEnv } from './deployEnv';
import { trackErrorInSentry } from './sentry';

export let journeyClient: JourneyClient | undefined;

let pageUnloaded = false;

export function initJourney(batchInterval: number = 5000) {
  // don't init journey client in server side rendering
  if (typeof window === 'undefined') {
    return;
  }

  journeyClient = new JourneyClient(batchInterval);
  journeyClient.start();

  window.addEventListener('beforeunload', () => {
    pageUnloaded = true;
    journeyClient?.send();
  });
}

export const trackUserEventInJourney = (userEvent: UserEvent, contextualData: ContextualData) => {
  const anonymousId = getAnonymousID();
  const url = typeof window !== 'undefined' ? window.location.href : '';
  // the type cast is safe here and is the only way to make Typescript happy
  const event = { $case: Object.keys(userEvent)[0] as any, ...userEvent };
  if (!anonymousId) {
    trackErrorInSentry('ajs_anonymous_id is empty or undefined', undefined, {
      ...contextualData,
      data: {
        ...contextualData.data,
        anonymousId,
        url,
        event,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      },
    });
  }

  const protobufUserEvent: PROTOBUF_UserEvent = {
    uuid: '', // journey generates this
    timestamp: new Date(),
    anonymousId,
    humanId: contextualData.user?.humanID,
    // extend when we add mobile
    source: {
      $case: 'websiteSource',
      websiteSource: { url },
    },
    event,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
  };
  journeyClient?.enqueue(JSON.stringify(PROTOBUF_UserEvent.toJSON(protobufUserEvent)));
};

// ajs_anonymous_id cookie is usually set by Segment, and if not, by Cloudflare worker
const getAnonymousID: () => string | undefined = () => {
  let anonymousID = Cookies.get('ajs_anonymous_id');
  if (!anonymousID) {
    return undefined;
  }
  // in prod, ajs_anonymous_id starts and ends with ", but in staging, it does not
  // it's unclear why, but we should handle both cases
  if (anonymousID.startsWith('"') && anonymousID.endsWith('"')) {
    anonymousID = anonymousID.slice(1, -1);
  }
  return anonymousID;
};

// journey http endpoint batch size limit is 512KB
const MAX_PAYLOAD_SIZE = 512;
// max number of requests in a batch
const MAX_NUM_REQUEST = 10;

export const JOURNEY_ENDPOINT =
  getDeployEnv() === 'production'
    ? 'https://journey.managed.services.opendoor.com/api/v1/user_event_batch'
    : 'https://journey-staging.managed.services.opendoor.com/api/v1/user_event_batch';

function computeSizeInKilobytes(buffer: string[]): number {
  const size = buffer.map((s) => s.length).reduce((prev, cur) => prev + cur, 0);
  return size / 1024;
}

/**
 * Checks if the payload is over or close to (within 50B of) the maximum payload size.
 */
function isApproachingBatchAPILimit(buffer: string[]): boolean {
  return computeSizeInKilobytes(buffer) >= MAX_PAYLOAD_SIZE - 50;
}

/**
 * TODO (billy): add to `shared-ts`
 * A wrapper around `fetch` with 5 retries at 500ms intervals
 */
const fetchWithRetry = async (url: string, init: RequestInit): Promise<void> => {
  let retries = 5;
  const retryDelay = 500;
  let response: Response | undefined = undefined;

  while (retries > 0) {
    try {
      response = await fetch(url, init);
      const statusCode = response.status;
      if (statusCode === 202) {
        return;
      }
      if (statusCode >= 500 && retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      } else {
        break;
      }
    } catch (err) {
      if (retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      }
    }
  }
  // no more retries left, report to Sentry
  Sentry.captureException(
    `failed to track events in journey, body: ${init.body}, statusCode: ${response?.status}`,
  );
};

class JourneyClient {
  private buffer: string[];
  private readonly batchInterval: number;
  private timer: NodeJS.Timer | undefined;
  constructor(batchInterval: number) {
    this.buffer = [];
    this.batchInterval = batchInterval;
  }

  start() {
    this.timer = setInterval(() => this.send(), this.batchInterval);
  }

  stop() {
    this.timer && clearInterval(this.timer);
  }

  send() {
    if (this.buffer.length === 0) {
      return;
    }
    fetchWithRetry(JOURNEY_ENDPOINT, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Journey-Client-Name': 'observability',
        'X-Journey-Client-Version': '1.0',
      },
      keepalive: pageUnloaded,
      method: 'POST',
      body: `{"userEvents":[${this.buffer.join(',')}]}`,
    });
    this.buffer = [];
  }

  enqueue(json: string): void {
    this.buffer.push(json);
    if (this.buffer.length >= MAX_NUM_REQUEST || isApproachingBatchAPILimit(this.buffer)) {
      this.send();
    }
  }
}
